import { ReactNode } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { CheckCircleOutlined, CloseCircleOutlined, CloseOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { DISPLAY_FONT, MONO_FONT, REDUCED_MOTION } from 'components/ui/tokens';

export const FREEZE_MONO = MONO_FONT;

// Ink of the always-midnight surface, fixed in both app themes. `low` clears only 3:1, so it is for
// decorative labels, never body copy.
export const PRESERVATION_INK = {
  surface: '#070B12',
  panel: '#0B1018',
  high: '#F2F6FB',
  medium: '#9FB0C4',
  low: '#5A6470',
  ice: '#A8E4FF',
  hairline: 'rgba(255,255,255,0.08)',
  line: 'rgba(255,255,255,0.14)',
  glass: 'rgba(255,255,255,0.035)',
  glassRaised: 'rgba(255,255,255,0.05)'
} as const;

export type FreezeTemperature = 'ice' | 'warm';

// Two temperatures: ice while the subscription goes on hold, amber while it comes back.
// The state reads at a glance, before the heading is read.
export const TEMPERATURE: Record<FreezeTemperature, { accent: string; soft: string; glow: string; ink: string }> = {
  ice: { accent: '#A8E4FF', soft: 'rgba(168,228,255,0.14)', glow: 'rgba(120,200,255,0.22)', ink: '#06121F' },
  warm: { accent: '#FFB877', soft: 'rgba(255,184,119,0.14)', glow: 'rgba(255,170,100,0.20)', ink: '#1F1206' }
};

// The card's structural rhythm, shared by the hero, the step rail, every body
// section and the footer so their left edges line up exactly.
export const FREEZE_LAYOUT = {
  cardRadius: '26px',
  panelRadius: '22px',
  tileRadius: '20px',
  controlRadius: '14px',
  pill: 999,
  gutterX: { xs: 2.5, sm: 4, md: 5.5 },
  bodyPaddingY: { xs: 3.5, md: 5 }
} as const;

/** The midnight body, with the aurora that drifts behind it. */
export const glacialSurfaceSx = (temperature: FreezeTemperature = 'ice') => ({
  position: 'relative' as const,
  isolation: 'isolate' as const,
  color: PRESERVATION_INK.high,
  backgroundColor: PRESERVATION_INK.surface,
  backgroundImage: [
    `radial-gradient(55% 45% at 85% 0%, ${TEMPERATURE[temperature].glow}, transparent 70%)`,
    `radial-gradient(40% 40% at 5% 100%, ${TEMPERATURE[temperature].soft}, transparent 70%)`
  ].join(',')
});

// The hero is the top of the same midnight surface as the body; it only adds the
// grid lines and a brighter aurora.
export const preservationHeroSx = (temperature: FreezeTemperature = 'ice') => ({
  ...glacialSurfaceSx(temperature),
  overflow: 'hidden' as const,
  backgroundImage: [
    `radial-gradient(680px 340px at 84% 6%, ${TEMPERATURE[temperature].glow}, transparent 68%)`,
    `linear-gradient(${PRESERVATION_INK.glass} 1px, transparent 1px)`,
    `linear-gradient(90deg, ${PRESERVATION_INK.glass} 1px, transparent 1px)`
  ].join(','),
  backgroundSize: 'auto, 56px 56px, 56px 56px'
});

export const freezePanelSx = (theme: Theme) => ({
  borderRadius: FREEZE_LAYOUT.panelRadius,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: theme.palette.background.paper
});

export const freezeReveal = (index = 0) => ({
  opacity: 0,
  animation: 'freezeRise 420ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards',
  animationDelay: `${index * 65}ms`,
  '@keyframes freezeRise': {
    from: { opacity: 0, transform: 'translateY(9px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  [REDUCED_MOTION]: { animation: 'none', opacity: 1 }
});

type MonoTone = 'accent' | 'muted' | 'danger' | 'ink';

const MONO_TONE_COLOR: Record<MonoTone, string> = {
  accent: 'primary.main',
  muted: 'text.secondary',
  danger: 'error.main',
  ink: PRESERVATION_INK.low
};

/** Uppercase micro-label. Every kicker, column header and tag in the flow is one. */
export function FreezeMonoLabel({
  children,
  tone = 'muted',
  size = 10,
  tracking = '0.14em'
}: {
  children: ReactNode;
  tone?: MonoTone;
  size?: number;
  tracking?: string;
}) {
  return (
    <Typography
      component="span"
      sx={{
        display: 'block',
        fontFamily: FREEZE_MONO,
        fontSize: `${size}px`,
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: tracking,
        textTransform: 'uppercase',
        color: MONO_TONE_COLOR[tone]
      }}
    >
      {children}
    </Typography>
  );
}

export type FreezeStatusTone = 'ice' | 'warm' | 'warning' | 'error';

/** Colour of a status, on the glacial surface. Ice while held, warm while thawing. */
export const freezeStatusColor = (tone: FreezeStatusTone) =>
  tone === 'ice' || tone === 'warm' ? TEMPERATURE[tone].accent : tone === 'warning' ? '#FFC48F' : '#FF9F93';

/** A frosted dot-and-label status pill: MUI's solid Chip reads as a stray green badge on a midnight
 *  page. */
export function FreezeStatusPill({ label, tone = 'ice' }: { label: string; tone?: FreezeStatusTone }) {
  const color = freezeStatusColor(tone);
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        display: 'inline-flex',
        py: 0.7,
        pl: 1.25,
        pr: 1.6,
        borderRadius: FREEZE_LAYOUT.pill,
        border: `1px solid ${alpha(color, 0.38)}`,
        bgcolor: alpha(color, 0.12)
      }}
    >
      <Box aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 0 3px ${alpha(color, 0.18)}` }} />
      <Typography
        sx={{
          fontFamily: FREEZE_MONO,
          fontSize: 10.5,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color,
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

type SectionTone = 'accent' | 'neutral' | 'danger';

/** Square icon tile that opens every section head and column header. */
export function FreezeIconTile({ tone = 'neutral', size = 40, children }: { tone?: SectionTone; size?: number; children: ReactNode }) {
  return (
    <Box
      sx={(theme) => {
        const color =
          tone === 'accent' ? theme.palette.primary.main : tone === 'danger' ? theme.palette.error.main : theme.palette.text.primary;
        return {
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: FREEZE_LAYOUT.tileRadius,
          display: 'grid',
          placeItems: 'center',
          fontSize: Math.round(size * 0.46),
          color: tone === 'neutral' ? 'text.primary' : color,
          border: `1px solid ${alpha(color, tone === 'neutral' ? 0.12 : 0.26)}`,
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.1 : 0.05)
        };
      }}
    >
      {children}
    </Box>
  );
}

export function FreezeSectionHead({
  kicker,
  tone = 'accent',
  title,
  description,
  icon
}: {
  kicker: string;
  tone?: SectionTone;
  title: string;
  description?: string;
  icon: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <FreezeIconTile tone={tone}>{icon}</FreezeIconTile>
      <Box sx={{ minWidth: 0 }}>
        <FreezeMonoLabel tone={tone === 'danger' ? 'danger' : tone === 'accent' ? 'accent' : 'muted'} size={11}>
          {kicker}
        </FreezeMonoLabel>
        <Typography
          component="h2"
          sx={{
            mt: 1.4,
            fontFamily: DISPLAY_FONT,
            fontSize: { xs: 24, sm: 30 },
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-0.03em'
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography sx={{ mt: 1, maxWidth: 520, fontSize: 15, lineHeight: 1.6, color: 'text.secondary' }}>{description}</Typography>
        )}
      </Box>
    </Stack>
  );
}

export type AlertTone = 'info' | 'success' | 'warning' | 'error';

const alertIcons: Record<AlertTone, ReactNode> = {
  info: <InfoCircleOutlined />,
  success: <CheckCircleOutlined />,
  warning: <WarningOutlined />,
  error: <CloseCircleOutlined />
};

/** A soft alert; with `onDismiss` it carries a close button named `dismissLabel`, and `action` sits under the text. */
export function FreezeSoftAlert({
  tone = 'info',
  title,
  children,
  onDismiss,
  dismissLabel,
  action
}: {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  action?: ReactNode;
}) {
  return (
    <Box
      role={tone === 'warning' || tone === 'error' ? 'alert' : 'status'}
      sx={(theme) => {
        const color = theme.palette[tone].main;
        return {
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
          borderRadius: '14px',
          p: 1.75,
          bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.1 : 0.06),
          border: `1px solid ${alpha(color, theme.palette.mode === 'dark' ? 0.24 : 0.18)}`
        };
      }}
    >
      <Box sx={{ display: 'flex', color: `${tone}.main`, fontSize: 17, mt: '2px', flexShrink: 0 }}>{alertIcons[tone]}</Box>
      <Box sx={{ minWidth: 0 }}>
        {title && <Typography sx={{ fontWeight: 700, mb: 0.25, lineHeight: 1.4 }}>{title}</Typography>}
        <Typography component="div" variant="body2" color="text.primary" sx={{ lineHeight: 1.65 }}>
          {children}
        </Typography>
        {action && <Box sx={{ mt: 1.25 }}>{action}</Box>}
      </Box>
      {onDismiss && (
        <IconButton
          size="small"
          aria-label={dismissLabel}
          onClick={onDismiss}
          sx={{ ml: 'auto', mt: '-4px', mr: '-4px', color: 'text.secondary' }}
        >
          <CloseOutlined />
        </IconButton>
      )}
    </Box>
  );
}

const CRYSTAL_FULL =
  'M12 2v20M2 12h20M4.9 4.9l14.2 14.2M19.1 4.9L4.9 19.1M12 6l-2.4-2.4M12 6l2.4-2.4M12 18l-2.4 2.4M12 18l2.4 2.4M6 12l-2.4-2.4M6 12l-2.4 2.4M18 12l2.4-2.4M18 12l2.4 2.4';
const CRYSTAL_SIMPLE = 'M12 2v20M3.5 7l17 10M20.5 7l-17 10';

export function SnowflakeMark({
  size = 24,
  detailed = false,
  strokeWidth = 1.6
}: {
  size?: number | string;
  detailed?: boolean;
  strokeWidth?: number;
}) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{ display: 'block', width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth, strokeLinecap: 'round' }}
    >
      <path d={detailed ? CRYSTAL_FULL : CRYSTAL_SIMPLE} />
    </Box>
  );
}
